import React from 'react';
import 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Box, Typography } from '@mui/material';
import { TypographySettings } from '../../../components/config';
import { formatNumber } from '../../../utilities';
import { sinceStartColor } from '../intervention-ladder/intervention-ladder-colors';

const textColor = '#5b6b73';
const gridColor = 'rgba(24, 76, 97, 0.08)';
const zeroLineColor = '#c9d2d8';
const directionLabel = { position: 'absolute', left: 0, fontSize: '0.6875rem', fontWeight: 600, color: textColor } as const;
const sinceStartIndex = 2;
const sinceStartAxis = 'sinceStart';
export const sinceStartLabel = 'Ændring siden start';

export function formatChange(change: number) {
  return change > 0 ? `+${formatNumber(change)}` : formatNumber(change);
}

interface TooltipItem {
  index: number;
  datasetIndex: number;
  value: string;
}

const datalabels = {
  display: (context: { dataIndex: number; dataset: { data: number[] } }) => context.dataset.data[context.dataIndex] !== 0,
  formatter: (value: number) => formatNumber(Math.abs(value)),
  anchor: 'end',
  align: 'end',
  offset: 1,
  color: '#16323d',
  font: { size: 11, weight: 600, family: TypographySettings.DefaultFontFamily },
};

export interface InterventionArrivalsSeries {
  label: string;
  counts: number[];
  barColors: string[];
  hoverColors: string[];
}

export interface InterventionArrivalsChartProps {
  labels: string[][];
  monthTitles: string[];
  arrivals: InterventionArrivalsSeries;
  departures: InterventionArrivalsSeries;
  sinceStart: number[];
}

export function changesSinceStart(arrivals: number[], departures: number[]) {
  let change = 0;
  return arrivals.map((arrival, index) => (change += arrival - departures[index]));
}

// Up to 5 the step is whole, e.g. grid lines at 4 and 8 when the highest month is 7; above it 10, 20, 50 and so on.
function gridStep(highest: number) {
  const half = highest / 2;
  if (half <= 5) {
    return Math.ceil(half);
  }
  const magnitude = 10 ** Math.floor(Math.log10(half));
  return [1, 2, 5, 10].map(factor => factor * magnitude).find(step => step >= half)!;
}

export function chartScales(arrivals: number[], departures: number[], sinceStart: number[]) {
  const highestBar = Math.max(1, ...arrivals, ...departures);
  const highestChange = Math.max(...sinceStart.map(Math.abs));
  // The line shares the bars' scale, so the grid lines count for it too, unless that would leave the bars less than half
  // their height; then it gets a scale of its own, and its end label gives its number.
  const sharesScale = highestChange <= 2 * highestBar;
  const highest = sharesScale ? Math.max(highestBar, highestChange) : highestBar;
  const step = gridStep(highest);
  const lineAbove = sharesScale ? sinceStart.filter(change => change > 0) : [];
  const lineBelow = sharesScale ? sinceStart.filter(change => change < 0).map(Math.abs) : [];
  // Each way goes as far as its own numbers, so a line far above zero leaves no empty half below it. A whole number of
  // steps keeps Chart.js from counting them from the bottom and missing 0, and the extra quarter leaves room for the
  // numbers past the bars.
  const max = step * Math.ceil((Math.max(1, ...arrivals, ...lineAbove) * 1.25) / step);
  const min = -step * Math.ceil((Math.max(1, ...departures, ...lineBelow) * 1.25) / step);
  // A scale of its own stretches the bars' scale evenly both ways, so zero stays at the same height on both.
  const stretch = sharesScale ? 1 : Math.max(1, ...sinceStart.map(change => (change * 1.25) / (change > 0 ? max : min)));
  return { step, min, max, sinceStartMin: min * stretch, sinceStartMax: max * stretch };
}

export function InterventionArrivalsChart({ labels, monthTitles, arrivals, departures, sinceStart }: InterventionArrivalsChartProps) {
  const { step, min, max, sinceStartMin, sinceStartMax } = chartScales(arrivals.counts, departures.counts, sinceStart);
  const tickCount = (max - min) / step + 1;
  // Chart.js also draws a line at the top and bottom of the axis, which the chart doesn't want.
  const gridColors = Array.from({ length: tickCount }, (_, index) => (index === 0 || index === tickCount - 1 ? 'transparent' : gridColor));
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    layout: { padding: { right: 28 } },
    scales: {
      xAxes: [
        {
          stacked: true,
          gridLines: { display: false, drawBorder: false },
          ticks: { autoSkip: false, maxRotation: 0, fontSize: 12, fontColor: textColor, fontFamily: TypographySettings.DefaultFontFamily },
          barPercentage: 1,
          categoryPercentage: 0.55,
        },
      ],
      yAxes: [
        {
          stacked: true,
          ticks: { display: false, min, max, stepSize: step },
          gridLines: { color: gridColors, zeroLineColor: zeroLineColor, drawBorder: false, drawTicks: false },
        },
        { id: sinceStartAxis, display: false, ticks: { min: sinceStartMin, max: sinceStartMax } },
      ],
    },
    tooltips: {
      mode: 'index',
      backgroundColor: '#16323d',
      displayColors: false,
      xPadding: 10,
      yPadding: 8,
      titleFontFamily: TypographySettings.DefaultFontFamily,
      bodyFontFamily: TypographySettings.DefaultFontFamily,
      callbacks: {
        title: (items: TooltipItem[]) => monthTitles[items[0].index],
        label: (item: TooltipItem) =>
          item.datasetIndex === sinceStartIndex
            ? `${sinceStartLabel}: ${formatChange(Number(item.value))} borgere (tilgang − afgang)`
            : `${[arrivals, departures][item.datasetIndex].label}: ${formatNumber(Math.abs(Number(item.value)))} borgere`,
      },
    },
    plugins: { datalabels },
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 240 }} aria-hidden>
      <Bar
        data={{
          labels,
          datasets: [
            { label: arrivals.label, data: arrivals.counts, backgroundColor: arrivals.barColors, hoverBackgroundColor: arrivals.hoverColors },
            {
              label: departures.label,
              data: departures.counts.map(count => -count),
              backgroundColor: departures.barColors,
              hoverBackgroundColor: departures.hoverColors,
              // The datalabels plugin takes a bar's end to be its top, also when the bar points down.
              datalabels: { anchor: 'start', align: 'bottom' },
            },
            {
              type: 'line',
              label: sinceStartLabel,
              data: sinceStart,
              yAxisID: sinceStartAxis,
              // A higher order is drawn first, so the line lies behind the bars.
              order: 1,
              fill: false,
              lineTension: 0,
              borderColor: sinceStartColor,
              borderWidth: 2,
              pointRadius: 3,
              pointHoverRadius: 3,
              pointBackgroundColor: sinceStartColor,
              pointHoverBackgroundColor: sinceStartColor,
              pointBorderColor: '#ffffff',
              pointHoverBorderColor: '#ffffff',
              pointBorderWidth: 1.5,
              // Only the end of the line is labelled, as that is where it is read.
              datalabels: {
                display: (context: { dataIndex: number }) => context.dataIndex === sinceStart.length - 1,
                formatter: formatChange,
                anchor: 'center',
                align: 'right',
                offset: 4,
                color: sinceStartColor,
                font: { size: 11, weight: 700, family: TypographySettings.DefaultFontFamily },
              },
            },
          ],
        }}
        options={options}
      />
      <Typography sx={{ ...directionLabel, top: 0 }}>Tilgang ↑</Typography>
      {/* Above the two lines of month and year under the chart. */}
      <Typography sx={{ ...directionLabel, bottom: 34 }}>Afgang ↓</Typography>
    </Box>
  );
}
